import { StyledOrdersDrawer } from '../style';
import { Transaction, TransactionsProps, DeliveryCondition, Fulfillment } from '../../../../interfaces/BackendModels';
import { OrderStatus } from './OrderStatus/OrderStatus';
import { OrderItemsTable } from './OrderItemsTable/OrderItemsTable';
import { OrderInvoiceBase } from './OrderInvoiceBase/OrderInvoiceBase';
import { DeliverySection } from './DeliverySection/DeliverySection';
import { useIntl } from 'react-intl';
import useQueryApiClient from '../../../../utils/useQueryApiClient';
import { Button, Icon, Modal, Spinner } from 'ui';
import { Form, Tooltip, message } from 'antd';
import useFarmOrderHandler from 'hooks/useFarmOrderHandler';
import { useState, useEffect, useMemo } from 'react';
import PublicRoute from 'enums/publicRoutes';

const WHOLESALE_DELIVERY_STATUSES = ['pending_send', 'partially_sent', 'sent'];

interface TransactionRow extends Transaction {
  has_rating?: boolean;
  transaction_type?: string;
  buyer_farm_name?: string;
  seller_farm_name?: string;
  delivery_type?: string;
  order_status?: string;
  horizon_href?: string;
  number?: string;
  created_date?: string;
}

interface TransactionsPropsExtended extends Omit<TransactionsProps, 'number' | 'order_id'> {
  has_rating?: boolean;
  transaction_type?: string;
  buyer_farm_name?: string;
  seller_farm_name?: string;
  delivery_type?: string;
  order_status?: string;
  horizon_href?: string;
  number?: string;
  created_date?: string;
  order_id?: number | string;
  delivery_conditions?: DeliveryCondition[];
}

interface OrdersViewOrderProps {
  transaction?: TransactionRow | TransactionsPropsExtended | Transaction | null;
  open?: boolean;
  onClose?: () => void;
  reloadPage?: number;
  setReload?: React.Dispatch<React.SetStateAction<number>>;
  refetchTransaction?: Function;
  showActionButton?: boolean;
  selectedTab?: string;
  onStatusChange?: (id: string, status: string) => void;
  onCancel?: (id: string) => void;
  onOpenRatingDrawer?: (transaction: TransactionRow, viewOnly: boolean) => void;
  onOpenMessageDrawer?: () => void;
  isAdmin?: boolean;
}

export const OrdersViewOrder = ({
  transaction,
  onClose,
  open,
  reloadPage,
  setReload,
  refetchTransaction,
  showActionButton = false,
  selectedTab = 'transactions',
  onStatusChange,
  onCancel,
  onOpenRatingDrawer,
  onOpenMessageDrawer,
  isAdmin = false,
}: OrdersViewOrderProps) => {
  const intl = useIntl();
  const { handleDownloadClick, handleDownloadFulfillmentInvoice, pdfLoading } = useFarmOrderHandler();
  const [isEditMode, setIsEditMode] = useState(false);
  const [showPaymentConfirmModal, setShowPaymentConfirmModal] = useState(false);
  const [showContentConfirmModal, setShowContentConfirmModal] = useState(false);
  const [, forceUpdate] = useState({});
  const [form] = Form.useForm();

  useEffect(() => {
    if (transaction) {
      const rawItems = transaction.items ?? [];
      const mergedItemsMap = new Map<number, typeof rawItems[0]>();

      rawItems.forEach((item) => {
        const existing = mergedItemsMap.get(item.product_id);
        if (existing) {
          const newCount = Number(existing.stock_count ?? 0) + Number(item.stock_count ?? 0);
          const newTotal = Number(existing.stock_total ?? 0) + Number(item.stock_total ?? 0);
          mergedItemsMap.set(item.product_id, {
            ...existing,
            stock_count: String(newCount),
            stock_total: String(newTotal),
          });
        } else {
          mergedItemsMap.set(item.product_id, { ...item });
        }
      });

      const formValues = {
        items: Array.from(mergedItemsMap.values()),
        deliveries: transaction.delivery_conditions?.map((dc) => ({
          id: dc.id,
          delivery_address: dc.delivery_address ?? '',
          delivery_price: Number(dc.delivery_price ?? 0),
        })) ?? [],
      };

      form.setFieldsValue(formValues);
      forceUpdate({});
    }
  }, [isEditMode, transaction, form]);

  const isWholesaleTransaction = ['purchase', 'reservation', 'dynamic_purchase', 'order'].includes(
    transaction?.transaction_type?.toLowerCase() ?? ''
  );
  const showPvn = !isWholesaleTransaction || !!(transaction as Transaction)?.farm?.pvn_nr;

  const canEdit =
    isWholesaleTransaction &&
    transaction?.status?.toLowerCase() === 'new' &&
    showActionButton &&
    selectedTab === 'transactions';

  const directFulfillments = (transaction as Transaction)?.fulfillments ?? [];
  const hasNonDraftFulfillment = directFulfillments.some((f) => !f.is_draft);

  const allProductsSent = useMemo(() => {
    const items = transaction?.items ?? [];
    const conditions = (transaction?.delivery_conditions ?? []) as DeliveryCondition[];
    if (items.length === 0) return false;

    const allFulfillments: Fulfillment[] = [];
    const seenFulfillmentIds = new Set<number>();

    directFulfillments.forEach((f) => {
      seenFulfillmentIds.add(f.id);
      allFulfillments.push(f);
    });

    conditions.forEach((dc) => {
      dc.fulfillments?.forEach((f) => {
        if (!seenFulfillmentIds.has(f.id)) {
          seenFulfillmentIds.add(f.id);
          allFulfillments.push(f);
        }
      });
    });

    const seenProductIds = new Set<number>();
    return items.every((item) => {
      if (seenProductIds.has(item.product_id)) return true;
      seenProductIds.add(item.product_id);

      const productItemIds = items.filter((i) => i.product_id === item.product_id).map((i) => i.id);
      const totalQty = items
        .filter((i) => i.product_id === item.product_id)
        .reduce((sum, i) => sum + Number(i.stock_count ?? 0), 0);

      let sentQty = 0;
      allFulfillments.forEach((f) => {
        if (f.status === 'sent') {
          f.products?.forEach((p) => {
            const orderItemId = p.id ?? p.order_item_id;
            if (orderItemId !== undefined && productItemIds.includes(orderItemId)) {
              sentQty += Number(p.pivot?.product_quantity ?? p.product_quantity ?? 0);
            }
          });
        }
      });

      return sentQty >= totalQty;
    });
  }, [transaction?.items, transaction?.delivery_conditions, directFulfillments]);

  const { appendData: changeStatus } = useQueryApiClient({
    request: {
      url: `api/v2/transactions/change-status`,
      method: 'POST',
    },
    onSuccess: () => {
      if (reloadPage && setReload) {
        setReload(reloadPage + 1);
      }

      if (refetchTransaction && transaction?.id) {
        refetchTransaction({}, { transactionId: transaction.id });
      }
    },
  });

  const { appendData: updateTransaction, isLoading: isSaving } = useQueryApiClient({
    request: {
      url: 'api/v2/transactions/:transaction',
      method: 'PATCH',
    },
    onSuccess: () => {
      setIsEditMode(false);
      if (refetchTransaction && transaction?.id) {
        refetchTransaction({}, { transactionId: transaction.id });
      }
    },
  });

  const { appendData: confirmPayment } = useQueryApiClient({
    request: {
      url: 'api/v2/transactions/change-billing-status',
      method: 'POST',
    },
    onSuccess: () => {
      if (refetchTransaction && transaction?.id) {
        refetchTransaction({}, { transactionId: transaction.id });
      }
    },
  });

  const { appendData: createFulfillment } = useQueryApiClient({
    request: {
      url: 'api/v2/transactions/fulfillments',
      method: 'POST',
    },
    onSuccess: () => {
      if (refetchTransaction && transaction?.id) {
        refetchTransaction({}, { transactionId: transaction.id });
      }
    },
  });

  const { appendData: updateFulfillment } = useQueryApiClient({
    request: {
      url: 'api/v2/transactions/fulfillments/:fulfillment',
      method: 'PATCH',
    },
    onSuccess: () => {
      if (refetchTransaction && transaction?.id) {
        refetchTransaction({}, { transactionId: transaction.id });
      }
    },
  });

  const { appendData: submitFulfillment } = useQueryApiClient({
    request: {
      url: 'api/v2/transactions/fulfillments/:fulfillment/submit',
      method: 'POST',
    },
    onSuccess: () => {
      if (refetchTransaction && transaction?.id) {
        refetchTransaction({}, { transactionId: transaction.id });
      }
    },
  });

  const { appendData: markFulfillmentSent } = useQueryApiClient({
    request: {
      url: 'api/v2/transactions/fulfillments/:fulfillment/mark-sent',
      method: 'POST',
    },
    onSuccess: () => {
      if (refetchTransaction && transaction?.id) {
        refetchTransaction({}, { transactionId: transaction.id });
      }
    },
  });

  const { appendData: deleteFulfillment } = useQueryApiClient({
    request: {
      url: 'api/v2/transactions/fulfillments/:fulfillment',
      method: 'DELETE',
    },
    onSuccess: () => {
      if (refetchTransaction && transaction?.id) {
        refetchTransaction({}, { transactionId: transaction.id });
      }
    },
    onError: (error) => {
      message.error({
        content: error?.error ?? intl.formatMessage({ id: 'fulfillment.delete_failed' }),
        duration: 8,
      });
    },
  });

  const { appendData: sendFulfillmentInvoice } = useQueryApiClient({
    request: {
      url: 'api/v2/transactions/fulfillments/:fulfillment/send-invoice',
      method: 'POST',
    },
    onSuccess: () => {
      if (refetchTransaction && transaction?.id) {
        refetchTransaction({}, { transactionId: transaction.id });
      }
    },
    onError: (error) => {
      message.error({
        content: error?.error ?? intl.formatMessage({ id: 'fulfillment.invoice_failed' }),
        duration: 8,
      });
    },
  });

  const { appendData: confirmFulfillmentPayment } = useQueryApiClient({
    request: {
      url: 'api/v2/transactions/fulfillments/:fulfillment/confirm-payment',
      method: 'POST',
    },
    onSuccess: () => {
      if (refetchTransaction && transaction?.id) {
        refetchTransaction({}, { transactionId: transaction.id });
      }
    },
  });

  const { appendData: repeatOrder, isLoading: isRepeatOrderLoading } = useQueryApiClient({
    request: {
      url: 'api/v2/transactions/:transaction/repeat-order',
      method: 'POST',
      disableOnMount: true,
    },
    onSuccess: (response) => {
      localStorage.setItem('pendingRepeatOrder', JSON.stringify(response));
      window.location.href = PublicRoute.WHOLESALE_CART;
    },
  });

  const handleCreateFulfillment = (transactionId: number) => {
    createFulfillment({ transaction_id: transactionId });
  };

  const handleUpdateFulfillment = (
    fulfillmentId: number,
    data: { expected_delivery_date?: string; products?: { order_item_id: number; product_quantity: number }[] }
  ) => {
    updateFulfillment(data, { fulfillment: fulfillmentId });
  };

  const handleSubmitFulfillment = (fulfillmentId: number) => {
    submitFulfillment({}, { fulfillment: fulfillmentId });
  };

  const handleMarkFulfillmentSent = (fulfillmentId: number) => {
    markFulfillmentSent({}, { fulfillment: fulfillmentId });
  };

  const handleDeleteFulfillment = (fulfillmentId: number) => {
    deleteFulfillment({}, { fulfillment: fulfillmentId });
  };

  const handleSendFulfillmentInvoice = (fulfillmentId: number) => {
    sendFulfillmentInvoice({}, { fulfillment: fulfillmentId });
  };

  const handleConfirmFulfillmentPayment = (fulfillmentId: number) => {
    confirmFulfillmentPayment({}, { fulfillment: fulfillmentId });
  };

  const handleProceedToShipping = () => {
    if (transaction?.id) {
      changeStatus({
        status: 'pending_send',
        id: transaction.id,
      });
    }
  };

  const handleSave = async () => {
    interface DeliveryFormValues {
      id?: number;
      delivery_address: string;
      delivery_price: number;
    }

    interface ItemFormValues {
      id?: number;
      product_id: number;
      product?: { id: number };
      product_category_id: number;
      product_subcategory_id: number;
      product_name: string;
      stock_price: number;
      stock_count: number;
      stock_unit: string;
      vat: number | null;
      delivery_address?: string;
    }

    const values = form.getFieldsValue();

    const deliveries: DeliveryFormValues[] = values.deliveries || [];
    const totalDeliveryPrice = deliveries.reduce(
      (sum, delivery) => sum + (delivery.delivery_price || 0),
      0
    );

    const deliveryAddress = deliveries[0]?.delivery_address;

    await updateTransaction(
      {
        transaction_id: transaction?.id,
        items: values.items.map((item: ItemFormValues) => {
          const productId = item.product_id || item.product?.id;

          if (!productId) {
            throw new Error(`Product ID missing for item: ${JSON.stringify(item)}`);
          }

          return {
            ...(item.id && { id: item.id }),
            product_id: productId,
            product_category_id: item.product_category_id,
            product_subcategory_id: item.product_subcategory_id,
            product_name: item.product_name,
            stock_price: item.stock_price,
            stock_count: item.stock_count,
            stock_unit: item.stock_unit,
            vat: item.vat != null ? Number(item.vat) : null,
            delivery_address: deliveryAddress || item.delivery_address,
          };
        }),
        delivery_price: totalDeliveryPrice,
        delivery_conditions: deliveries.map((d) => ({
          ...(d.id && { id: d.id }),
          delivery_address: d.delivery_address,
          delivery_price: d.delivery_price,
        })),
      },
      { transaction: transaction?.id }
    );
  };

  const handleCancel = () => {
    setIsEditMode(false);
  };

  const handleClose = () => {
    setIsEditMode(false);
    onClose?.();
  };

  const handleConfirmPayment = () => {
    confirmPayment({ id: transaction?.id, status: '1' });
  };

  const renderTitle = (invoiceNr: string) => {
    return (
      <div className={'custom-header'}>
        <span>{intl.formatMessage({ id: selectedTab === 'my_orders' ? 'general.order' : 'general.transaction_title' })}{invoiceNr}</span>
      </div>
    );
  };

  const handleOnDownloadClick = () => {
    if (!transaction) return;

    let downloadData;
    if ('transactions' in transaction && transaction.transactions) {
      downloadData = transaction.transactions
        .filter((data: Transaction) => data.order_id && data.farm_id)
        .map((data: Transaction) => ({
          order_id: String(data.order_id),
          farm_id: data.farm_id,
        }));
    } else if (transaction.order_id && transaction.farm_id) {
      downloadData = [
        {
          order_id: String(transaction.order_id),
          farm_id: transaction.farm_id,
        },
      ];
    } else {
      return;
    }

    handleDownloadClick(downloadData);
  };

  const Footer = () => {
    const hasRating = transaction?.has_rating;
    const isCompleted = transaction?.status?.toLowerCase() === 'delivered';
    const isCancelled = ['canceled', 'failed', 'rejected'].includes(
      transaction?.status?.toLowerCase() ?? ''
    );
    const isTransactionsTab = selectedTab === 'transactions';
    const isMyOrdersTab = selectedTab === 'my_orders';
    const status = transaction?.status?.toLowerCase();
    const canCancel = status === 'new' || status === 'confirmed';
    const canDownloadInvoice = ['confirmed', 'sent', 'delivered', 'pending_send', 'partially_sent'].includes(status ?? '');
    const isPaid = transaction?.billing_status === 1;
    const isInDeliveryManagement = WHOLESALE_DELIVERY_STATUSES.includes(status ?? '');

    return (
      <div className="footer-container">
        {isEditMode ? (
          <>
            <Button
              label={intl.formatMessage({ id: 'general.cancel' })}
              onClick={handleCancel}
            />
            <Button
              label={intl.formatMessage({ id: 'general.save' })}
              type="primary"
              onClick={handleSave}
              loading={isSaving}
            />
          </>
        ) : (
          <>
            {isTransactionsTab && (
              <>
                {!isWholesaleTransaction && (
                  <Button
                    label={
                      <Spinner spinning={pdfLoading}>
                        {intl.formatMessage({ id: 'orders.download' })}
                      </Spinner>
                    }
                    onClick={handleOnDownloadClick}
                  />
                )}

                {onOpenMessageDrawer && (
                  <Button
                    label={intl.formatMessage({ id: 'general.send_message' })}
                    onClick={onOpenMessageDrawer}
                  />
                )}
              </>
            )}

            {canEdit && (
              <Button
                label={intl.formatMessage({ id: 'general.edit' })}
                onClick={() => setIsEditMode(true)}
              />
            )}

            {showActionButton && isTransactionsTab && transaction?.id && (
              <>
                {status === 'new' && (
                  <>
                    <Button
                      label={intl.formatMessage({ id: 'enum.CONFIRMED' })}
                      type="primary"
                      onClick={() => {
                        if (isWholesaleTransaction) {
                          setShowContentConfirmModal(true);
                        } else {
                          onStatusChange?.(String(transaction.id), 'confirmed');
                        }
                      }}
                    />
                    <Button
                      label={intl.formatMessage({ id: 'general.cancel_transaction' })}
                      onClick={() => onCancel?.(String(transaction.id))}
                    />
                  </>
                )}
                {status === 'confirmed' && (
                  <>
                    {isAdmin && (
                      <Button
                        label={intl.formatMessage({ id: 'enum.NEW' })}
                        onClick={() => onStatusChange?.(String(transaction.id), 'new')}
                      />
                    )}
                    {isWholesaleTransaction ? (
                      <Tooltip
                        title={
                          !hasNonDraftFulfillment
                            ? intl.formatMessage({ id: 'orders.proceed_to_shipping_disabled_hint' })
                            : ''
                        }
                      >
                        <span>
                          <Button
                            label={intl.formatMessage({ id: 'orders.proceed_to_shipping' })}
                            type="primary"
                            onClick={handleProceedToShipping}
                            disabled={!hasNonDraftFulfillment}
                          />
                        </span>
                      </Tooltip>
                    ) : (
                      <Button
                        label={intl.formatMessage({ id: 'enum.SENT' })}
                        type="primary"
                        onClick={() => onStatusChange?.(String(transaction.id), 'sent')}
                      />
                    )}
                    <Button
                      label={intl.formatMessage({ id: 'general.cancel_transaction' })}
                      onClick={() => onCancel?.(String(transaction.id))}
                    />
                  </>
                )}
                {isInDeliveryManagement && isWholesaleTransaction && (
                  <>
                    {isAdmin && (
                      <Button
                        label={intl.formatMessage({ id: 'enum.CONFIRMED' })}
                        onClick={() => onStatusChange?.(String(transaction.id), 'confirmed')}
                      />
                    )}
                    {allProductsSent && (
                      <Tooltip
                        title={
                          !isPaid
                            ? intl.formatMessage({ id: 'error.invoice_not_paid' })
                            : undefined
                        }
                      >
                        <span>
                          <Button
                            label={intl.formatMessage({ id: 'transactions.completed' })}
                            type="primary"
                            onClick={() => onStatusChange?.(String(transaction.id), 'delivered')}
                            disabled={!isPaid}
                          />
                        </span>
                      </Tooltip>
                    )}
                  </>
                )}
                {status === 'sent' && !isWholesaleTransaction && (
                  <>
                    {isAdmin && (
                      <Button
                        label={intl.formatMessage({ id: 'enum.CONFIRMED' })}
                        onClick={() => onStatusChange?.(String(transaction.id), 'confirmed')}
                      />
                    )}
                    <Button
                      label={intl.formatMessage({ id: 'transactions.completed' })}
                      type="primary"
                      onClick={() => onStatusChange?.(String(transaction.id), 'delivered')}
                    />
                  </>
                )}
                {status === 'delivered' && isAdmin && (
                  <Button
                    label={intl.formatMessage({ id: 'enum.SENT' })}
                    onClick={() => onStatusChange?.(String(transaction.id), 'sent')}
                  />
                )}
              </>
            )}

            {isMyOrdersTab && transaction?.id && (
              <>
                {status === 'confirmed' && isWholesaleTransaction && directFulfillments.length === 0 && (
                  <Button
                    label={intl.formatMessage({ id: 'orders.order_products' })}
                    type="primary"
                    onClick={() => handleCreateFulfillment(transaction.id)}
                  />
                )}

                {onOpenMessageDrawer && (
                  <Button
                    label={intl.formatMessage({ id: 'general.send_message' })}
                    onClick={onOpenMessageDrawer}
                  />
                )}

                {canCancel && onCancel && (
                  <Button
                    label={intl.formatMessage({ id: 'general.cancel_order' })}
                    onClick={() => onCancel(String(transaction.id))}
                  />
                )}

                {canDownloadInvoice && !isWholesaleTransaction && (
                  <Button
                    label={
                      <Spinner spinning={pdfLoading}>
                        {intl.formatMessage({ id: 'general.download_invoice' })}
                      </Spinner>
                    }
                    onClick={handleOnDownloadClick}
                    type={canCancel ? 'default' : 'primary'}
                  />
                )}

                {isCompleted && isWholesaleTransaction && (
                  <Button
                    label={intl.formatMessage({ id: 'orders.repeat_order' })}
                    type="primary"
                    loading={isRepeatOrderLoading}
                    onClick={() => repeatOrder({}, { transaction: transaction.id })}
                  />
                )}

                {onOpenRatingDrawer && transaction?.transaction_type && (
                  <>
                    {hasRating ? (
                      <>
                        <Button
                          label={intl.formatMessage({ id: 'rating.view' })}
                          type="primary"
                          onClick={() => onOpenRatingDrawer(transaction as TransactionRow, true)}
                        />
                        <Button
                          label={intl.formatMessage({ id: 'rating.edit' })}
                          onClick={() => onOpenRatingDrawer(transaction as TransactionRow, false)}
                        />
                      </>
                    ) : (isCompleted || isCancelled) ? (
                      <Button
                        label={intl.formatMessage({ id: 'rating.give' })}
                        type="primary"
                        onClick={() => onOpenRatingDrawer(transaction as TransactionRow, false)}
                      />
                    ) : null}
                  </>
                )}
              </>
            )}
          </>
        )}
      </div>
    );
  };

  return (
    <StyledOrdersDrawer
      open={open}
      onClose={handleClose}
      width={isAdmin ? 1025 : 923}
      title={renderTitle(transaction?.invoice_nr ?? '')}
      footer={<Footer />}
      zIndex={1100}
      closeIcon={<Icon name={'global-close-icon'} />}
    >
      <Form form={form} className={`order-content${isEditMode ? ' edit-mode' : ''}`}>
        <OrderStatus transactionStatus={transaction?.status ?? 'new'} isWholesale={isWholesaleTransaction} />
        {!(isWholesaleTransaction && WHOLESALE_DELIVERY_STATUSES.includes(transaction?.status?.toLowerCase() ?? '')) && (
          <OrderItemsTable
            products={transaction?.items ?? []}
            isEditMode={isEditMode}
            farmId={transaction?.farm_id}
            deliveryConditions={transaction?.delivery_conditions as DeliveryCondition[] | undefined}
            transactionStatus={transaction?.status}
            showPvn={showPvn}
          />
        )}
        <DeliverySection
          isEditMode={isEditMode && !(isWholesaleTransaction && transaction?.status?.toLowerCase() === 'new')}
          transactionStatus={transaction?.status}
          deliveryConditions={transaction?.delivery_conditions as DeliveryCondition[] | undefined}
          fulfillments={(transaction as Transaction)?.fulfillments}
          transactionId={transaction?.id}
          onCreateFulfillment={handleCreateFulfillment}
          onUpdateFulfillment={handleUpdateFulfillment}
          onMarkFulfillmentSent={handleMarkFulfillmentSent}
          onDeleteFulfillment={handleDeleteFulfillment}
          onSubmitFulfillment={handleSubmitFulfillment}
          onSendFulfillmentInvoice={handleSendFulfillmentInvoice}
          onConfirmFulfillmentPayment={handleConfirmFulfillmentPayment}
          onDownloadFulfillmentInvoice={handleDownloadFulfillmentInvoice}
          isWholesale={isWholesaleTransaction}
          items={transaction?.items}
          isProvider={selectedTab === 'transactions'}
        />
        <div className={'content-invoice'}>
          {transaction && 'transactions' in transaction && transaction.transactions ? (
            transaction.transactions.map((trans: Transaction) => {
              return <OrderInvoiceBase transaction={trans} order={transaction} selectedTab={selectedTab} />;
            })
          ) : (
            <OrderInvoiceBase transaction={transaction as Transaction} selectedTab={selectedTab} />
          )}
        </div>
      </Form>
      <Modal
        open={showPaymentConfirmModal}
        onCancel={() => setShowPaymentConfirmModal(false)}
        title={null}
        footer={null}
        closable={false}
        width="fit-content"
        className="delivery-warning-modal"
      >
        <div className="delivery-warning-modal-content">
          <p>{intl.formatMessage({ id: 'orders.payment_confirm_question' })}</p>
          <div className="delivery-warning-modal-buttons">
            <Button
              label={intl.formatMessage({ id: 'general.back' })}
              onClick={() => setShowPaymentConfirmModal(false)}
            />
            <Button
              type="primary"
              label={intl.formatMessage({ id: 'orders.confirm_payment_action' })}
              onClick={() => {
                setShowPaymentConfirmModal(false);
                handleConfirmPayment();
              }}
            />
          </div>
        </div>
      </Modal>
      <Modal
        open={showContentConfirmModal}
        onCancel={() => setShowContentConfirmModal(false)}
        title={null}
        footer={null}
        closable={false}
        width="fit-content"
        className="delivery-warning-modal"
      >
        <div className="delivery-warning-modal-content">
          <p>{intl.formatMessage({ id: 'orders.content_confirm_question' })}</p>
          <div className="delivery-warning-modal-buttons">
            <Button
              label={intl.formatMessage({ id: 'general.back' })}
              onClick={() => setShowContentConfirmModal(false)}
            />
            <Button
              type="primary"
              label={intl.formatMessage({ id: 'general.confirm' })}
              onClick={() => {
                setShowContentConfirmModal(false);
                if (onStatusChange) {
                  onStatusChange(String(transaction?.id), 'confirmed');
                } else {
                  changeStatus({ status: 'confirmed', id: transaction?.id });
                }
              }}
            />
          </div>
        </div>
      </Modal>
    </StyledOrdersDrawer>
  );
};
